// One ledger row in full (GET /lessons/{id}): the human verdict that was
// judged, the lesson the agent wrote to its Knowledge Bank, the questions it
// asked, its reasoning text, and the complete audit (reflection prompt, every
// tool call, the raw turn result). Nothing is truncated here either.

import React from 'react';

import {
  SOURCE_DISPLAY,
  type LedgerDetail,
  type TurnToolUse,
} from '../../services/feedbackLearning';
import styles from '../AgentEvalPage.module.css';
import { formatTs, formatUsd, pretty } from './format';

function Pre({ value, testId }: { value: unknown; testId?: string }): React.ReactElement {
  return (
    <pre className={styles['audit-pre']} data-testid={testId}>
      {pretty(value)}
    </pre>
  );
}

function ToolUseList({ uses }: { uses: TurnToolUse[] }): React.ReactElement {
  if (uses.length === 0) return <div className={styles.empty}>(no tool calls)</div>;
  return (
    <ol className={styles['tool-use-list']}>
      {uses.map((u, i) => (
        <li key={u.tool_use_id ?? i}>
          <div className={styles['tool-use-head']}>
            <b>{u.name}</b>
            {u.is_error && <span className={`${styles.tag} ${styles.errored}`}>rejected</span>}
          </div>
          <Pre value={u.input} />
        </li>
      ))}
    </ol>
  );
}

export function LessonDetailView({
  detail,
  onBack,
}: {
  detail: LedgerDetail;
  onBack: () => void;
}): React.ReactElement {
  const signal = detail.signal ?? {};
  const turn = detail.turn_result;
  const kbWrites = detail.kb_writes ?? [];
  const questions = detail.questions ?? [];
  const toolUses = turn?.tool_uses ?? [];

  return (
    <div className={styles.detail} data-testid="lesson-detail">
      <button
        type="button"
        className={styles['back-link']}
        onClick={onBack}
        data-action="agent_eval_lesson_back"
        data-outcome="close"
      >
        ← back to lessons
      </button>
      <h2>{signal.headline ?? `${SOURCE_DISPLAY[detail.source] ?? detail.source} · ${detail.source_ref}`}</h2>
      <div className={styles.meta}>
        <span>
          source: <b>{SOURCE_DISPLAY[detail.source] ?? detail.source}</b>
        </span>
        <span>
          status: <b>{detail.status}</b>
        </span>
        <span>
          attempts: <b>{detail.attempts}</b>
        </span>
        <span>
          profile: <b>{detail.profile_id ?? '—'}</b>
        </span>
        <span>
          model: <b>{detail.model ?? '—'}</b>
        </span>
        <span>
          cost: <b>{formatUsd(detail.cost_usd)}</b>
        </span>
        <span>
          tokens:{' '}
          <b>
            {detail.input_tokens ?? '—'} in / {detail.output_tokens ?? '—'} out
          </b>
        </span>
        <span>
          created: <b>{formatTs(detail.created_at)}</b>
        </span>
        <span>
          completed: <b>{formatTs(detail.completed_at)}</b>
        </span>
        <span>
          source ref: <b>{detail.source_ref}</b>
        </span>
        <span>
          source version: <b>{formatTs(detail.source_version)}</b>
        </span>
        <span>
          conversation: <b>{detail.conversation_id ?? '— (not resumable)'}</b>
        </span>
        {detail.eval_case_id && (
          <span>
            eval case: <b>{detail.eval_case_id}</b>
          </span>
        )}
      </div>

      {detail.error && (
        <div className={styles.error} data-testid="lesson-error">
          {detail.error}
        </div>
      )}

      <div className={styles['verdict-card']} data-section="human_verdict" data-testid="human-verdict">
        <h3>What the person judged</h3>
        <div>
          <b>Verdict:</b> {signal.verdict ?? '—'}
        </div>
        {signal.human_notes && (
          <div>
            <b>Their words:</b> {signal.human_notes}
          </div>
        )}
        <div className={styles.meta} style={{ marginTop: 6, marginBottom: 0 }}>
          <span>
            by: <b>{signal.actor ?? '—'}</b>
          </span>
          <span>
            at: <b>{formatTs(signal.occurred_at)}</b>
          </span>
          {signal.kind && (
            <span>
              kind: <b>{signal.kind}</b>
            </span>
          )}
        </div>
      </div>

      <h3>Lesson written to the Knowledge Bank</h3>
      {kbWrites.length === 0 ? (
        <div className={styles.empty} data-testid="no-kb-write">
          No Knowledge-Bank write: the agent concluded there was nothing new to record.
        </div>
      ) : (
        <ol className={styles['tool-use-list']} data-testid="kb-writes">
          {kbWrites.map((w, i) => (
            <li key={i}>
              <div className={styles['tool-use-head']}>
                <b>{w.bare_tool}</b>
                {w.mount && <span className={`${styles.tag} ${styles.pending}`}>{w.mount}</span>}
              </div>
              <Pre value={w.input} />
            </li>
          ))}
        </ol>
      )}

      {questions.length > 0 && (
        <>
          <h3>Questions the agent asked</h3>
          <ul className={styles['question-list']} data-testid="questions">
            {questions.map((q, i) => (
              <li key={i}>
                <div>{q.question}</div>
                {q.context && <div className={styles.sub}>{q.context}</div>}
              </li>
            ))}
          </ul>
          {detail.question_delivery && (
            <details>
              <summary>How the questions were delivered</summary>
              <Pre value={detail.question_delivery} />
            </details>
          )}
        </>
      )}

      <h3>Agent's reasoning</h3>
      {turn?.text ? (
        <div className={styles.rationale} data-testid="rationale">
          {turn.text}
        </div>
      ) : (
        <div className={styles.empty}>(no reflection text recorded)</div>
      )}

      <h3>Full audit</h3>
      <details data-section="audit_prompt">
        <summary>Reflection prompt</summary>
        {detail.reflection_prompt ? (
          <Pre value={detail.reflection_prompt} testId="reflection-prompt" />
        ) : (
          <div className={styles.empty}>(no prompt recorded)</div>
        )}
      </details>
      <details data-section="audit_tool_calls">
        <summary>Tool calls ({toolUses.length})</summary>
        <ToolUseList uses={toolUses} />
      </details>
      <details data-section="audit_signal">
        <summary>Signal as given to the agent (state, audit trail, links)</summary>
        <Pre value={signal} testId="signal-json" />
      </details>
      <details data-section="audit_turn_result">
        <summary>Raw turn result</summary>
        {turn ? <Pre value={turn} testId="turn-json" /> : <div className={styles.empty}>(no turn result)</div>}
      </details>
    </div>
  );
}
